import { watch } from 'vue'
import { doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore'
import { auth, db } from '@/firebase'
import { useAuthStore } from '@/store/authStore'
import { useSchoolStore } from '@/store/schoolStore'
import { auditLogger } from '@/utils/auditLogger'
import {
  overlaySecuritySettings,
  systemSettingsWriteTarget,
  usesSchoolOverride
} from '@/utils/schoolSystemSettings'

export function activeSchoolId() {
  try {
    return useSchoolStore().currentSchoolId || null
  } catch {
    return null
  }
}

/**
 * District security doc, with the current school's securitySettings laid over
 * it for school-scoped people. Returns an unsubscribe function.
 */
export function listenEffectiveSecurity({ onEffective, onDistrictMissing, onError }) {
  let district = null
  let districtReady = false
  let school = null
  let schoolUnsub = null

  const emit = () => {
    if (!districtReady) return
    let applySchool
    try {
      applySchool = usesSchoolOverride(useAuthStore().currentUser)
    } catch {
      applySchool = false
    }
    onEffective(overlaySecuritySettings(district, applySchool ? school : null))
  }

  const unsubDistrict = onSnapshot(
    doc(db, 'app_settings', 'security'),
    (snap) => {
      if (snap.exists()) {
        district = snap.data()
      } else {
        district = null
        onDistrictMissing?.()
      }
      districtReady = true
      emit()
    },
    (error) => {
      districtReady = false
      console.error('Failed to load security settings:', error)
      onError?.(error)
    }
  )

  let stopWatch = () => {}
  try {
    const schoolStore = useSchoolStore()
    const authStore = useAuthStore()
    stopWatch = watch(
      () => {
        const user = authStore.currentUser
        return usesSchoolOverride(user) ? schoolStore.currentSchoolId : null
      },
      (schoolId) => {
        if (schoolUnsub) schoolUnsub()
        schoolUnsub = null
        school = null
        if (!schoolId) {
          emit()
          return
        }
        schoolUnsub = onSnapshot(
          doc(db, 'schools', schoolId),
          (snap) => {
            school = snap.exists() ? snap.data()?.securitySettings || null : null
            emit()
          },
          (error) => {
            console.error('Failed to load school security settings:', error)
            onError?.(error)
          }
        )
      },
      { immediate: true }
    )
  } catch (error) {
    onError?.(error)
  }

  return () => {
    unsubDistrict()
    if (schoolUnsub) schoolUnsub()
    stopWatch()
  }
}

export async function writeSecurityFields(user, schoolId, fields) {
  const target = systemSettingsWriteTarget(user, schoolId)
  const meta = {
    updatedAt: new Date().toISOString(),
    updatedBy: auth.currentUser?.uid || null
  }
  const scope = { schoolId: target.scope === 'school' ? target.schoolId : null }
  const details = { scope: target.scope, fields: Object.keys(fields) }
  try {
    if (target.scope === 'district') {
      await setDoc(doc(db, 'app_settings', 'security'), { ...fields, ...meta }, { merge: true })
    } else {
      const nested = {
        'securitySettings.updatedAt': meta.updatedAt,
        'securitySettings.updatedBy': meta.updatedBy
      }
      for (const [key, value] of Object.entries(fields)) {
        nested[`securitySettings.${key}`] = value
      }
      await updateDoc(doc(db, 'schools', target.schoolId), nested)
    }
    await auditLogger.logSystemAccess('security_settings_saved', details, scope)
    return target
  } catch (error) {
    await auditLogger.logSystemAccess(
      'security_settings_save_failed',
      { ...details, errorCode: error.code || 'unknown' },
      scope
    )
    throw error
  }
}
